/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

// Unistyles 3.0 SSR bridge for the Next.js App Router: collects the CSS the
// server render produced and streams it into the document, so the page
// arrives fully styled. Without it the server HTML carries `unistyles_*`
// class names with no stylesheet and the first paint is unstyled until
// hydration. Wrap `{children}` of the root layout's <body> with it.
//
// Deliberately NOT the stock useServerUnistyles() hook: that hook snapshots
// the registry in the wrapper's render body — before any page component
// (and its StyleSheet.create calls) has rendered — which yields an empty
// stylesheet here. Snapshotting inside useServerInsertedHTML runs at stream
// flush time, after the whole tree rendered.

import { useRef, type PropsWithChildren } from 'react';
import { useServerInsertedHTML } from 'next/navigation';
import {
  getServerUnistyles,
  hydrateServerUnistyles,
  resetServerUnistyles,
} from 'react-native-unistyles/server';
// Registers themes/breakpoints (StyleSheet.configure) before any StyleSheet
// is created during this render pass.
import { initThemes } from '@airasia-rn/ui/config/unistyles';

initThemes();

export default function UnistylesSSR({ children }: PropsWithChildren) {
  const isServerInserted = useRef(false);

  useServerInsertedHTML(() => {
    if (isServerInserted.current) {
      return null;
    }
    isServerInserted.current = true;
    const components = getServerUnistyles();
    resetServerUnistyles();
    return components;
  });

  if (typeof window !== 'undefined') {
    hydrateServerUnistyles();
  }

  return <>{children}</>;
}
